<template>
  <DialogComponent
    :modelValue="modelValue"
    @update:modelValue="handleModelValueUpdate"
    :cancelModalOnClickAway="false"
    :aria-labelledby="dialogTitleId">
    <Form
      @submit="save"
      v-slot="{ errors }">
      <div class="space-y-8">
        <h1
          :id="dialogTitleId"
          class="font-bitter text-3xl font-bold mb-8">
          {{header}}
        </h1>
        <template v-if="reviewer.type === ReviewerType.Recommend">
          <p class="mb-6">
            Recommended reviewers must have sufficient expertise in the relevant subject area <strong>and be able to provide an unbiased assessment of the manuscript</strong>. Recommended reviewers should not be at the same institute as any of the authors listed on the manuscript <strong>or have been a colleague, close collaborator, joint grant holder, or part of a mentoring relationship with any of the listed authors within the last 3 years.</strong>
          </p>
          <p class="mb-6">
            The RSC considers diversity in reviewers to be an important aspect of peer review. Therefore, wherever possible please consider diversity when recommending reviewers, for example in terms of career stage, gender, geographic location, race and ethnicity, etc.
          </p>
          <p class="mb-6">
            Institute email addresses should be provided for recommended reviewers, wherever possible.
          </p>
        </template>
        <h2 class="font-roboto text-2xl font-bold mb-4">
          Contact Information
        </h2>
        <Field
          :rules="`required:You must enter a first name|max-characters-length:${limits.reviewers.characters.firstName},Reviewer first name must not exceed`"
          name="firstname"
          v-slot="{ field, errorMessage }"
          :value="reviewer.firstName">
          <FormInput
            label="First name"
            data-testid="test-recommended-reviewer-first-name"
            :id="firstNameTextBoxId"
            v-model="reviewer.firstName"
            :error-message="errorMessage ? errorMessage : ''"
            v-bind="field"
            class="data-hj-suppress"/>
        </Field>
        <Field
          :rules="`required:You must enter a last name|max-characters-length:${limits.reviewers.characters.lastName},Reviewer last name must not exceed`"
          name="lastname"
          v-slot="{ field, errorMessage }"
          :value="reviewer.lastName">
          <FormInput
            label="Last name"
            data-testid="test-recommended-reviewer-last-name"
            :id="lastNameTextBoxId"
            v-model="reviewer.lastName"
            :error-message="errorMessage ? errorMessage : ''"
            v-bind="field"
            class="data-hj-suppress"/>
        </Field>
        <Field
          :rules="`email|not-duplicated-mail-edit-author|not-duplicated-mail-edit-reviewer:${reviewer.id}|max-characters-length:${limits.reviewers.characters.email},Email address must not exceed`"
          name="email"
          v-slot="{ field, errorMessage }"
          :value="reviewer.email">
          <FormInput
            label="Email address"
            data-testid="test-recommended-reviewer-email-address"
            :id="emailTextBoxId"
            type="email"
            v-model="reviewer.email"
            :error-message="errorMessage ? errorMessage : ''"
            v-bind="field"
            class="data-hj-suppress"/>
        </Field>
        <Field
          name="reason"
          v-slot="{ field }"
          :rules="`max-characters-length:${limits.reviewers.characters.reason},You must not exceed`"
          :value="reviewer.reason"
        >
          <FormTextArea
            v-model="reviewer.reason"
            label="Reason"
            :help-text="reasonHelpText"
            data-testid="test-recommended-reviewer-reason"
            class="data-hj-suppress"
            v-bind="field"
            :rows="2"
            :is-optional="true"
            :error-message="errors.reason ? errors.reason : ''"
            :id="reasonTextBox"
          />
        </Field>
        <TextCounter
          :text="reviewer.reason"
          mode="characters"
          :max-count="limits.reviewers.characters.reason"
        />
      </div>
      <div class="pt-8">
        <ButtonComponent
          label="Save"
          variant="primary"
          data-testid="save-recommended-reviewer"
          :id="nextButton"
          class="w-full md:w-auto"
          type="submit"
        />
        <ButtonComponent
          label="Delete this reviewer"
          variant="tertiary"
          v-if="canDelete"
          class="w-full md:w-auto mt-2"
          @customClick="deleteReviewer"
          data-testid="delete-reviewer"
        />
      </div>
    </Form>
  </DialogComponent>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'
import { Reviewer, ReviewerType } from '@/types'
import { Form, Field } from 'vee-validate'
import ButtonComponent from '../ui-components/ButtonComponent.vue'
import FormTextArea from '../ui-components/FormTextArea.vue'
import deepClone from '../../services/deepClone'
import FormInput from '../ui-components/FormInput.vue'
import DialogComponent from '../ui-components/DialogComponent.vue'
import TextCounter from '../common/TextCounter.vue'
import limits from '../../constants/limits'
import { useReviewerManagement } from '@/composables/useReviewerManagement'

const emit = defineEmits(['close', 'delete', 'save', 'update:modelValue'])

const props = defineProps({
  modelValue: { required: true, type: Boolean },
  value: { required: true, type: Object as () => Reviewer },
})

const {
  formatReviewerFields,
  getReviewerDialogHeader,
  getReasonHelpText,
  canDeleteReviewer,
  getReviewerPosition,
  generateReviewerId,
} = useReviewerManagement()

const reviewer = reactive(deepClone(props.value))
const reviewerPosition = getReviewerPosition(props.value.id, props.value.type)

const dialogTitleId = generateReviewerId(reviewer.type, reviewerPosition, 'TitleLabel')
const firstNameTextBoxId = generateReviewerId(reviewer.type, reviewerPosition, 'FirstNameTextBox')
const lastNameTextBoxId = generateReviewerId(reviewer.type, reviewerPosition, 'LastNameTextBox')
const emailTextBoxId = generateReviewerId(reviewer.type, reviewerPosition, 'EmailTextBox')
const reasonTextBox = generateReviewerId(reviewer.type, reviewerPosition, 'ReasonTextBox')
const nextButton = generateReviewerId(reviewer.type, reviewerPosition, 'NextButton')

function save() {
  formatReviewerFields(reviewer)
  emit('save', reviewer)
  close()
}

function deleteReviewer() {
  emit('delete', reviewer)
  close()
}

function close() {
  emit('update:modelValue', false)
}

function handleModelValueUpdate(value: boolean) {
  emit('update:modelValue', value)
  if (!value) {
    emit('close')
  }
}

const header = computed(() => getReviewerDialogHeader(reviewer.type))
const reasonHelpText = computed(() => getReasonHelpText(reviewer.type))
const canDelete = computed(() => canDeleteReviewer(reviewer.id))
</script>
